import { useEffect, type ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

interface Props {
  children: ReactElement;
  needsBusiness?: boolean;
  needsAdmin?: boolean;
}

/**
 * Route guard for /app/* routes.
 *
 * Previously this used a hardcoded 1200 ms grace timer to wait for the
 * Supabase business fetch after sign-in. On slow connections (3G in
 * Gaborone, Lagos, etc.) the fetch sometimes took longer than 1.2 s,
 * which bounced authenticated users to /signup on direct navigation or
 * page refresh. Replaced with a definitive `hasLoadedBusiness` flag in
 * the auth store that flips to true only after the round-trip completes.
 */
export function RequireAuth({ children, needsBusiness = true, needsAdmin = false }: Props) {
  const session = useAuthStore((s) => s.session);
  const profile = useAuthStore((s) => s.profile);
  const business = useAuthStore((s) => s.business);
  const loading = useAuthStore((s) => s.loading);
  const hasLoadedBusiness = useAuthStore((s) => s.hasLoadedBusiness);
  const init = useAuthStore((s) => s.init);
  const location = useLocation();

  useEffect(() => {
    if (loading) void init();
  }, [loading, init]);

  // Show the spinner while:
  //   - the initial getSession() is still in flight, or
  //   - we have a session but the profile/business round-trip has not
  //     completed yet (only relevant when this guard cares about the business).
  if (loading || (session && needsBusiness && !hasLoadedBusiness)) {
    return (
      <main className="container-mobile py-20 text-center">
        <p className="text-muted">Loading…</p>
      </main>
    );
  }

  if (!session) return <Navigate to="/" replace state={{ from: location }} />;
  if (needsAdmin && !profile?.is_admin) return <Navigate to="/app" replace />;
  if (needsBusiness && !business) return <Navigate to="/signup" replace />;

  return children;
}
